import * as React from "react";
import { Upload, X } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "./button";

export interface FilePickerProps {
  accept?: string;
  value?: File | null;
  onChange?: (file: File | null) => void;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  maxSize?: number; // in bytes, default 10MB
}

const DEFAULT_MAX_SIZE = 10 * 1024 * 1024; // 10MB

/**
 * FilePicker component with drag & drop support
 */
export const FilePicker = React.forwardRef<HTMLDivElement, FilePickerProps>(
  (
    {
      accept = ".csv,text/csv",
      value,
      onChange,
      disabled = false,
      error,
      helperText,
      maxSize = DEFAULT_MAX_SIZE,
      ...props
    },
    ref,
  ) => {
    const [isDragging, setIsDragging] = React.useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleFileSelect = (file: File | null) => {
      if (!file) {
        onChange?.(null);
        return;
      }

      // Check file size
      if (file.size > maxSize) {
        // Error will be handled by parent component
        return;
      }

      // Check file type
      if (
        accept &&
        !accept.split(",").some((type) => {
          const trimmed = type.trim();
          if (trimmed.startsWith(".")) {
            return file.name.toLowerCase().endsWith(trimmed.toLowerCase());
          }
          return file.type === trimmed;
        })
      ) {
        // Error will be handled by parent component
        return;
      }

      onChange?.(file);
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileSelect(files[0]);
      }
    };

    const handleRemove = () => {
      onChange?.(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    const formatFileSize = (bytes: number): string => {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
      <div ref={ref} {...props}>
        {!value ? (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              "relative flex flex-col items-center justify-center rounded-md border-2 border-dashed p-8 transition-colors",
              isDragging && !disabled
                ? "border-primary bg-primary/5"
                : "border-input bg-muted/30",
              disabled && "opacity-50 cursor-not-allowed",
              error && "border-destructive",
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={accept}
              onChange={handleInputChange}
              disabled={disabled}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
            />
            <Upload
              className={cn(
                "h-8 w-8 mb-3",
                error ? "text-destructive" : "text-muted-foreground",
              )}
            />
            <p className="text-sm font-semibold text-foreground mb-1">
              {isDragging ? "Drop file here" : "Drag & drop file here"}
            </p>
            {helperText && (
              <p className="text-xs text-muted-foreground mb-4 text-center max-w-sm">
                {helperText}
              </p>
            )}
            {!helperText && (
              <p className="text-xs text-muted-foreground mb-4">or</p>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
            >
              Browse Files
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-3 rounded-md border border-input bg-background p-3 transition-colors hover:bg-muted/50">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {value.name}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatFileSize(value.size)}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleRemove}
              disabled={disabled}
              className="h-8 w-8 shrink-0"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Remove file</span>
            </Button>
          </div>
        )}
        {error && <p className="text-sm text-destructive mt-2">{error}</p>}
      </div>
    );
  },
);

FilePicker.displayName = "FilePicker";
